import { isSignOutApiBlocked } from "@sukha/query/signOutGate";

import { getApiToken } from "@/lib/api/authToken";

/**
 * Attaches `Authorization: Bearer …` to every `/api/v1/*` request the browser
 * makes, whoever makes it (TX-4856).
 *
 * ## Why an interceptor, and not "finish the seam"
 *
 * The issue assumed one client: *"the seam already exists — `apiRequest()`
 * threads an optional token — so this is finishing it, not building it."*
 * There are four. `apiRequest` (19 files), `webApiFetcher` (everything
 * `@sukha/query` drives), `memberFetch` (4 files), and **40 files that call
 * `fetch("/api/v1/…")` directly**.
 *
 * Threading a token through all of those is a very large diff whose size is
 * entirely accidental, and — worse — it would not STAY true. The next
 * `fetch("/api/v1/…")` someone writes silently goes back to cookie auth, and
 * nothing fails until the API moves origin, at which point everything fails at
 * once. That is the failure this migration exists to avoid.
 *
 * One choke point makes the property categorical instead of a thing to audit.
 *
 * ## What it deliberately does not touch
 *
 *  - **Anything not `/api/v1/*`** — Clerk's own endpoints, UploadThing's
 *    ingest, images. Sending our token to a third party would be a
 *    credential leak, so the path check is an allow-list and not a guess.
 *  - **Cross-origin requests.** Same reason, and it is why the check runs on a
 *    resolved URL rather than the string that was passed in: `//evil.test/api/v1/x`
 *    is a protocol-relative URL to somebody else.
 *  - **A request that already carries an `Authorization` header.** A caller
 *    that chose an identity keeps it.
 */

let installed = false;

/** Restores the original `fetch`. For tests. */
export type UninstallApiAuthFetch = () => void;

export function installApiAuthFetch(): UninstallApiAuthFetch {
  if (typeof window === "undefined") return () => {};
  if (installed) return () => {};
  installed = true;

  const original = window.fetch.bind(window);

  const patched: typeof window.fetch = async (input, init) => {
    /*
     * The sign-out gate, enforced where it is categorical.
     *
     * `apiRequest`, `webApiFetcher` and `memberFetch` each already refuse when
     * `isSignOutApiBlocked()` — but a raw `fetch(apiUrl(...))` goes through
     * none of them, and that is how the weather widget kept asking
     * `/v1/weather/current` on the way out and collecting a 401. Putting the
     * check here covers every caller for the same reason the token is attached
     * here: it is the only place all of them pass through.
     *
     * `isSignOutApiBlocked()` and not `isClientSignOutInProgress()`, because
     * `runSignOutTeardownOps` deliberately opens a window for the end-session
     * and activity calls that sign-out itself has to make. The narrower
     * predicate is what respects it.
     *
     * The same `AbortError` the other three throw, so a caller that already
     * handles one handles this, and TanStack treats it as a cancellation
     * rather than a failure worth retrying or reporting.
     *
     * Clerk is untouched: this runs behind `isOurApiRequest`, so sign-out's own
     * requests are never blocked by it — blocking those would strand the very
     * teardown that sets the flag.
     */
    if (isOurApiRequest(input) && isSignOutApiBlocked()) {
      throw new DOMException("Sign-out in progress", "AbortError");
    }

    if (!wantsOurToken(input, init)) return original(input, init);

    const token = await getApiToken();
    if (!token) return original(input, init);

    let response = await original(input, withBearer(init, token));

    // One refresh-and-retry on 401, and only when the fresh token differs —
    // the same narrow rule the other clients use. A session token lives about
    // a minute, so an idle tab must not read as a sign-out; but retrying a
    // genuine 401 would double every failure and hide the first response.
    if (response.status === 401) {
      const refreshed = await getApiToken({ skipCache: true });
      if (refreshed && refreshed !== token) {
        response = await original(input, withBearer(init, refreshed));
      }
    }
    return response;
  };

  window.fetch = patched;
  return () => {
    window.fetch = original;
    installed = false;
  };
}

function withBearer(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

/**
 * `/api/uploadthing` is ours too and is NOT under `/v1` — the path is fixed by
 * UploadThing's client helpers and its callback. It is served by `apps/api`
 * through the same rewrite as everything else (TX-4869), so its upload request
 * needs the same token; the SDK does its own fetch, which is why the
 * interceptor is the only place that can attach one.
 */
function isOurApiPath(pathname: string): boolean {
  return pathname.startsWith("/api/v1/") || pathname === "/api/uploadthing";
}

/**
 * Is this a request to OUR API, whoever is making it and however it is
 * authorized?
 *
 * Split out of `wantsOurToken` because the sign-out gate below must not care
 * about the Authorization header: a caller that sets its own bearer (the genui
 * `authedPostJson`, for one) is still a member API call and still must not
 * leave during teardown.
 */
function isOurApiRequest(input: RequestInfo | URL): boolean {
  const url = resolveUrl(input);
  if (!url) return false;
  /*
   * Our own origins only — still an allow-list, never "any host".
   *
   * This was same-origin only, which was right while web served the API and
   * wrong the moment the browser started calling `api.thesukha.app` directly:
   * the token stopped being attached and every write answered 401. The API
   * origin is added explicitly rather than by loosening the rule, because the
   * reason for the rule has not changed — sending our bearer to a third party
   * would be a credential leak.
   */
  const apiOrigin = process.env.NEXT_PUBLIC_API_ORIGIN?.trim();
  const allowed = new Set([window.location.origin]);
  if (apiOrigin) {
    try {
      allowed.add(new URL(apiOrigin).origin);
    } catch {
      // A malformed variable must not widen the allow-list.
    }
  }
  if (!allowed.has(url.origin)) return false;
  return isOurApiPath(url.pathname);
}

/** A request that also has no identity of its own, so ours should be attached. */
function wantsOurToken(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): boolean {
  if (!isOurApiRequest(input)) return false;

  const existing = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined)
  );
  return !existing.has("authorization");
}

function resolveUrl(input: RequestInfo | URL): URL | null {
  try {
    if (typeof input === "string") return new URL(input, window.location.origin);
    if (input instanceof URL) return input;
    return new URL(input.url, window.location.origin);
  } catch {
    return null;
  }
}
